import { useState, useEffect } from 'react'
import { useConsultations } from '@/hooks/useConsultations'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { Consultation, ConsultationUpdate } from '@/types'
import { Cie10Combobox } from './Cie10Combobox'
import { ClarifyingNotesSection } from './ClarifyingNotesSection'
import { EvolutionStatusPicker } from './EvolutionStatus'
import { VITALS } from '@/lib/vitals'
import type { EvolutionStatus } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/hooks/use-toast'

const inputClass =
  'w-full bg-transparent border-0 border-b border-ml-outline-variant/40 focus:ring-0 focus:border-ml-primary px-0 py-2 text-ml-on-surface text-sm outline-none transition-colors'
const labelClass =
  'text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold block mb-1'

interface ConsultationFormProps {
  consultation: Consultation
  isAdmin?: boolean
  isAuthor?: boolean
  onSaved?: () => void
}

// Vital fields come from the shared registry (@/lib/vitals) so the set the
// clinician records can never drift from the set that is trended (REQ-FE-06).
const VITALS_FIELDS = VITALS

const STATUS_CONFIG: Record<
  Consultation['status'],
  { label: string; className: string }
> = {
  in_progress: {
    label: 'En curso',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  completed: {
    label: 'Completada',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  locked: {
    label: 'Bloqueada',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  unlock_requested: {
    label: 'Desbloqueo solicitado',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
}

export function ConsultationForm({
  consultation,
  isAdmin = false,
  isAuthor = false,
  onSaved,
}: ConsultationFormProps) {
  const { formatDate } = useClinicFormat()
  const { useUpdateConsultation, useCompleteConsultation, useRequestUnlock, useApproveUnlock } =
    useConsultations()
  const updateConsultation = useUpdateConsultation()
  const completeConsultation = useCompleteConsultation()
  const requestUnlock = useRequestUnlock()
  const approveUnlock = useApproveUnlock()

  const [chiefComplaint, setChiefComplaint] = useState(consultation.chief_complaint ?? '')
  const [subjective, setSubjective] = useState(consultation.subjective_notes ?? '')
  const [objective, setObjective] = useState(consultation.objective_notes ?? '')
  const [assessment, setAssessment] = useState(consultation.assessment ?? '')
  const [plan, setPlan] = useState(consultation.plan_notes ?? '')
  const [prognosis, setPrognosis] = useState(consultation.prognosis ?? '')
  const [evolutionStatus, setEvolutionStatus] = useState<EvolutionStatus | ''>(
    consultation.evolution_status ?? ''
  )
  const [vitals, setVitals] = useState<Record<string, string | number>>(consultation.vitals ?? {})
  const [diagnosisCodes, setDiagnosisCodes] = useState<string[]>(
    consultation.diagnosis_codes.length > 0 ? consultation.diagnosis_codes : []
  )
  const [unlockReason, setUnlockReason] = useState('')
  const [showUnlockInput, setShowUnlockInput] = useState(false)

  // Hydrate state only when switching to a different consultation. Using the
  // whole `consultation` object as a dep caused every React Query refetch to
  // clobber the user's in-progress edits (and stole focus mid-keystroke).
  useEffect(() => {
    setChiefComplaint(consultation.chief_complaint ?? '')
    setSubjective(consultation.subjective_notes ?? '')
    setObjective(consultation.objective_notes ?? '')
    setAssessment(consultation.assessment ?? '')
    setPlan(consultation.plan_notes ?? '')
    setPrognosis(consultation.prognosis ?? '')
    setEvolutionStatus(consultation.evolution_status ?? '')
    setVitals(consultation.vitals ?? {})
    setDiagnosisCodes(
      consultation.diagnosis_codes.length > 0 ? consultation.diagnosis_codes : []
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation.id])

  // NOM-004 REQ-INT-01 / NO-01: once signed (completed/locked) the note is
  // immutable. Corrections go through clarifying notes (addenda), never edits.
  const isSigned =
    consultation.status === 'completed' ||
    consultation.status === 'locked' ||
    consultation.status === 'unlock_requested'
  const isReadOnly = isSigned
  const statusConfig = STATUS_CONFIG[consultation.status]

  const buildUpdateData = (): ConsultationUpdate => {
    const nonEmptyCodes = diagnosisCodes.filter((c) => c.trim() !== '')
    return {
      chief_complaint: chiefComplaint,
      subjective_notes: subjective,
      objective_notes: objective,
      assessment,
      plan_notes: plan,
      prognosis,
      evolution_status: evolutionStatus,
      diagnosis_codes: nonEmptyCodes,
      vitals,
    }
  }

  const handleSaveDraft = async () => {
    try {
      await updateConsultation.mutateAsync({ id: consultation.id, data: buildUpdateData() })
      toast({ title: 'Borrador guardado', description: 'La consulta se guardó correctamente.' })
      onSaved?.()
    } catch (error: any) {
      toast({
        title: 'No pudimos guardar los cambios',
        description: error.message || error.response?.data?.detail || 'No fue posible guardar la consulta.',
        variant: 'destructive',
      })
    }
  }

  const handleComplete = async () => {
    try {
      // Save current state first, then complete
      await updateConsultation.mutateAsync({ id: consultation.id, data: buildUpdateData() })
      await completeConsultation.mutateAsync(consultation.id)
      toast({ title: 'Consulta completada', description: 'La consulta quedó finalizada y bloqueada.' })
      onSaved?.()
    } catch (error: any) {
      toast({
        title: 'No pudimos completar la consulta',
        description: error.message || error.response?.data?.detail || 'No fue posible completar la consulta.',
        variant: 'destructive',
      })
    }
  }

  const handleRequestUnlock = async () => {
    if (!unlockReason.trim()) {
      toast({ title: 'Motivo requerido', description: 'Escribe el motivo de la solicitud de desbloqueo.', variant: 'destructive' })
      return
    }
    try {
      await requestUnlock.mutateAsync({ id: consultation.id, reason: unlockReason.trim() })
      toast({ title: 'Solicitud enviada', description: 'La solicitud de desbloqueo se envió correctamente.' })
      setShowUnlockInput(false)
      setUnlockReason('')
      onSaved?.()
    } catch (error: any) {
      toast({
        title: 'No pudimos solicitar el desbloqueo',
        description: error.message || error.response?.data?.detail || 'No fue posible solicitar el desbloqueo.',
        variant: 'destructive',
      })
    }
  }

  const handleApproveUnlock = async () => {
    try {
      await approveUnlock.mutateAsync(consultation.id)
      toast({ title: 'Desbloqueo aprobado', description: 'La consulta quedó disponible para edición.' })
      onSaved?.()
    } catch (error: any) {
      toast({
        title: 'No pudimos aprobar el desbloqueo',
        description: error.message || error.response?.data?.detail || 'No fue posible aprobar el desbloqueo.',
        variant: 'destructive',
      })
    }
  }

  const updateVital = (key: string, value: string) => {
    setVitals((prev) => {
      if (value === '') {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: isNaN(Number(value)) ? value : Number(value) }
    })
  }

  const hasValidDiagnosisCodes = diagnosisCodes.length > 0

  return (
    <div className="space-y-6">
      {/* Status Badge */}
      <div className="flex items-center gap-3">
        <Badge variant="outline" className={statusConfig.className}>
          {statusConfig.label}
        </Badge>
        {consultation.status === 'locked' && consultation.locked_at && (
          <span className="text-xs text-ml-on-surface-variant/60">
            Bloqueada el {formatDate(consultation.locked_at)}
          </span>
        )}
        {consultation.status === 'unlock_requested' && consultation.unlock_reason && (
          <span className="text-xs text-ml-on-surface-variant/60">
            Motivo: {consultation.unlock_reason}
          </span>
        )}
      </div>

      {/* Chief Complaint */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Motivo de consulta</CardTitle>
        </CardHeader>
        <CardContent>
          <label className={labelClass}>Motivo principal de la consulta</label>
          <Input
            className={inputClass}
            value={chiefComplaint}
            onChange={(e) => setChiefComplaint(e.target.value)}
            placeholder="Describe el motivo principal de la consulta"
            disabled={isReadOnly}
          />
        </CardContent>
      </Card>

      {/* SOAP Notes */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Notas clínicas SOAP</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className={labelClass}>Subjetivo (S)</label>
            <Textarea
              className={inputClass + ' min-h-[80px] resize-y'}
              value={subjective}
              onChange={(e) => setSubjective(e.target.value)}
              placeholder="Síntomas, antecedentes y comentarios referidos por el paciente"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className={labelClass}>Objetivo (O)</label>
            <Textarea
              className={inputClass + ' min-h-[80px] resize-y'}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder="Hallazgos de exploración, signos vitales y resultados relevantes"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className={labelClass}>Valoración clínica (A)</label>
            <Textarea
              className={inputClass + ' min-h-[80px] resize-y'}
              value={assessment}
              onChange={(e) => setAssessment(e.target.value)}
              placeholder="Impresión diagnóstica, diagnósticos diferenciales y valoración clínica"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className={labelClass}>Tratamiento y plan terapéutico (P)</label>
            <Textarea
              className={inputClass + ' min-h-[80px] resize-y'}
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              placeholder="Tratamiento indicado, seguimiento, recomendaciones y plan terapéutico"
              disabled={isReadOnly}
            />
          </div>
          <div>
            {/* NOM-004 6.2 — pronóstico */}
            <label className={labelClass}>Pronóstico</label>
            <Textarea
              className={inputClass + ' min-h-[60px] resize-y'}
              value={prognosis}
              onChange={(e) => setPrognosis(e.target.value)}
              placeholder="Pronóstico del padecimiento (p. ej. favorable, reservado)"
              disabled={isReadOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* Evolution indicator vs. previous visit (improving / stable / worsening) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Evolución respecto a la visita anterior</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-ml-on-surface-variant/70 mb-3">
            Marca la evolución del paciente en esta visita. Alimenta el resumen de evolución.
          </p>
          <EvolutionStatusPicker
            value={evolutionStatus}
            onChange={setEvolutionStatus}
            disabled={isReadOnly}
          />
        </CardContent>
      </Card>

      {/* Vitals */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Signos vitales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {VITALS_FIELDS.map(({ key, label, unit }) => (
              <div key={key}>
                <label className={labelClass}>
                  {label} ({unit})
                </label>
                <Input
                  className={inputClass}
                  type="number"
                  step="any"
                  value={vitals[key] ?? ''}
                  onChange={(e) => updateVital(key, e.target.value)}
                  placeholder="—"
                  disabled={isReadOnly}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ICD-10 Diagnosis Codes */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-ml-on-surface">
          Códigos diagnósticos CIE-10
        </label>
        <Cie10Combobox
          selectedCodes={diagnosisCodes}
          onCodesChange={setDiagnosisCodes}
          disabled={isReadOnly}
        />
        {diagnosisCodes.length === 0 && (
          <p className="text-xs text-ml-on-surface-variant/60 italic">
            Se requiere al menos un código CIE-10 para completar la consulta.
          </p>
        )}
      </div>

      {/* Physician attestation + integrity hash (NOM-004 REQ-INT-02) */}
      {consultation.attested_by_name && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900 space-y-1">
          <div>
            <span className="font-semibold">Firmada por:</span> {consultation.attested_by_name}
            {consultation.attested_by_cedula && (
              <> · Cédula profesional {consultation.attested_by_cedula}</>
            )}
            {consultation.attested_at && (
              <> · {new Date(consultation.attested_at).toLocaleString('es-MX')}</>
            )}
          </div>
          {consultation.content_hash && (
            <div className="text-[11px] font-mono text-emerald-700/80 break-all">
              <span className="font-semibold not-italic">Hash de integridad (SHA-256):</span>{' '}
              {consultation.content_hash}
            </div>
          )}
        </div>
      )}

      {/* Clarifying notes (addenda) — corrections to a signed note (REQ-INT-03) */}
      {isSigned && (
        <ClarifyingNotesSection consultationId={consultation.id} canAdd={isAuthor || isAdmin} />
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {/* NO-01: a signed note shows no edit/save action. Only an in-progress
            draft can be saved; corrections to signed notes go via addenda. */}
        {consultation.status === 'in_progress' && (
          <Button
            onClick={handleSaveDraft}
            disabled={updateConsultation.isPending}
            variant="outline"
          >
            {updateConsultation.isPending ? 'Guardando...' : 'Guardar borrador'}
          </Button>
        )}

        {consultation.status === 'in_progress' && (
          <Button
            onClick={handleComplete}
            disabled={!hasValidDiagnosisCodes || completeConsultation.isPending}
          >
            {completeConsultation.isPending ? 'Firmando...' : 'Firmar y completar'}
          </Button>
        )}

        {consultation.status === 'locked' && isAuthor && (
          <>
            {showUnlockInput ? (
              <div className="flex items-center gap-2 w-full">
                <Input
                  className="flex-1"
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value)}
                  placeholder="Escribe el motivo de la solicitud..."
                />
                <Button
                  onClick={handleRequestUnlock}
                  disabled={requestUnlock.isPending}
                  size="sm"
                >
                  {requestUnlock.isPending ? 'Enviando...' : 'Enviar'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUnlockInput(false)
                    setUnlockReason('')
                  }}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setShowUnlockInput(true)}>
                Solicitar desbloqueo
              </Button>
            )}
          </>
        )}

        {consultation.status === 'unlock_requested' && isAdmin && (
          <Button onClick={handleApproveUnlock} disabled={approveUnlock.isPending}>
            {approveUnlock.isPending ? 'Aprobando...' : 'Aprobar desbloqueo'}
          </Button>
        )}
      </div>
    </div>
  )
}
